import { useCallback, useEffect, useMemo, useState } from "react"
import {
  Row,
  Col,
  Card,
  CardBody,
  Form,
  Container,
  Button,
  Input,
  FormGroup,
  Label,
} from "reactstrap"
import { useFormik } from "formik"
import * as Yup from "yup"
import "./QuotationAddEdit.css"
import Breadcrumbs from "components/Common/Breadcrumb"
import { useNavigate, useParams } from "react-router-dom"
import {
  useCreateQuotation,
  useEditQuotation,
  useQuotation,
} from "hooks/useQuotation"
import { toast } from "react-toastify"

// Custom Components
import DynamicField from "components/Common/DynamicField"
import DraggableList from "components/Common/DraggableList"
import { useDynamicForm } from "hooks/useDynamicForm"
import { convertToWords } from "helpers/number_helpers"
import { formatSinceDate } from "utils/formatDate"
import QuotationHeader from "./Quotation/QuotationHeader"
import { CustomerSelector } from "./Quotation/Customer"
import { useCustomer, useCustomerSearchAPI } from "hooks/useCustomer"
import VehicleSpecification from "./Quotation/Vehicle/VehicleSpecification"
import { useMediaQuery } from "react-responsive"
import { prepareVehicleStockFromSpecs } from "helpers/quotation_helpers"
import { useListParams } from "hooks/useListParams"
import { useVehicleStock } from "hooks/stockHook"

const QuotationAddEdit = () => {
  document.title = "Quotation Form | Aurobil Cars"

  const isMobile = useMediaQuery({ maxWidth: 767 })

  const navigate = useNavigate()
  const { identifer } = useParams()
  const isEdit = !!identifer
  const [multiCarMode, setMultiCarMode] = useState(false)
  const [printData, setPrintData] = useState(null)
  const [vehicleSpecs, setVehicleSpecs] = useState([])
  const [multiCarSpecs, setMultiCarSpecs] = useState([])
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [selectedVehicle, setSelectedVehicle] = useState(null)

  const { params } = useListParams()

  const { data: customerData, isLoading: customerLoading } = useCustomer(
    params.cuid,
    !!params.cuid
  )

  const { data: vehicleData, isLoading: vehicleLoading } = useVehicleStock(
    params.stock_id
  )

  // Hooks
  const { data: quotationResponse, isLoading } = useQuotation(identifer, {
    enabled: isEdit,
  })
  const { mutateAsync: createQuotation, isPending: createLoading } =
    useCreateQuotation()
  const { mutateAsync: editQuotation, isPending: editLoading } =
    useEditQuotation()

  const {
    items: priceItems,
    addItem: addPriceItem,
    removeItem: removePriceItem,
    updateItem: updatePriceItem,
    resetItems: resetPriceItems,
  } = useDynamicForm([
    { id: "1", description: "Unit Price", amount: "" },
    { id: "2", description: "Registration Cost", amount: "" },
  ])

  // Calculate total
  const totalAmount = useMemo(() => {
    if (multiCarMode) {
      return multiCarSpecs.reduce(
        (sum, car) => sum + (Number(car.unitPrice) || 0),
        0
      )
    }
    return priceItems.reduce((sum, item) => sum + (Number(item.amount) || 0), 0)
  }, [priceItems, multiCarSpecs, multiCarMode])

  const amountInWords = useMemo(() => {
    return convertToWords(totalAmount) + " Taka Only"
  }, [totalAmount])

  const quotation = quotationResponse?.data ?? {}

  // Initial form values
  const initialValues = useMemo(
    () => ({
      quotation_date:
        quotation?.quotation_date || new Date().toISOString().split("T")[0],
      valid_until:
        quotation?.valid_until ||
        new Date(Date.now() + 90 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0],
      customer_info: quotation?.customer_info || "",
      subject:
        quotation?.subject ||
        `Quotation for ${
          multiCarMode ? multiCarSpecs?.length : 1
        } (${convertToWords(
          multiCarMode ? multiCarSpecs?.length : 1
        )}) unit reconditioned vehicle`,
      body_text:
        quotation?.body_text ||
        "Dear Sir,\nWe are pleased to provide you with a quotation for the reconditioned vehicle(s) you are interested in. Please find the details below:",
      payment_terms:
        quotation?.payment_terms ||
        "• 50% advance payment to confirm booking\n• Balance payment before delivery\n• Payment via bank transfer or cash",
      closing_text:
        quotation?.closing_text ||
        "<p>Please do not hesitate to contact us if you have any questions or require further information. Our sales representatives are here to assist you in making the best choice.</p><p>Thank you for considering <b><i>AUROBIL CARS</i></b> for your reconditioned vehicle purchase. We look forward to serving your automotive needs.</p>",
      signatory_name: quotation?.signatory_name || "Redwan Ahmad",
      customer_signatory_name: quotation?.signatory_name || "",
      signatory_designation:
        quotation?.signatory_designation || "Proprietor - AUROBIL CARS",
    }),
    [quotation, multiCarMode, multiCarSpecs]
  )

  // Formik setup
  const validationSchema = Yup.object({
    quotation_date: Yup.date().required("Quotation date is required"),
    customer_info: Yup.string()
      .required("Customer information is required")
      .test("is-not-empty", "Customer information is required", value => {
        if (!value) return false
        const textContent = value.replace(/<[^>]*>/g, "").trim()
        return textContent.length > 0
      }),
    subject: Yup.string().required("Subject is required"),
    body_text: Yup.string().required("Body text is required"),
    payment_terms: Yup.string().required("Payment terms are required"),
    closing_text: Yup.string().required("Closing text is required"),
    signatory_name: Yup.string().required("Signatory name is required"),
    signatory_designation: Yup.string().required("Designation is required"),
  })

  const onSubmit = useCallback(
    async values => {
      try {
        const filteredVehicleSpecs = vehicleSpecs.filter(
          spec => spec.value.trim() !== ""
        )

        const pdfContent = {
          ...values,
          vehicle_specs: filteredVehicleSpecs,
          price_items: priceItems,
          multi_car_specs: multiCarMode ? multiCarSpecs : [],
          total_amount: totalAmount,
          amount_in_words: amountInWords,
          multi_car_mode: multiCarMode,
        }
        setPrintData(pdfContent)

        const payload = {
          pdf_content: pdfContent,
          customer_id: selectedCustomer?.id ?? null,
          new_customer: selectedCustomer ?? null,
          vehicle_id: selectedVehicle?.id ?? null,
          new_vehicle:
            prepareVehicleStockFromSpecs(pdfContent.vehicle_specs) ?? null,
        }

        const res = isEdit
          ? await editQuotation({ id: identifer, ...payload })
          : await createQuotation(payload)

        toast.success(
          res?.message ??
            `Quotation ${isEdit ? "updated" : "created"} successfully.`
        )
        navigate("/quotation-list")
      } catch (error) {
        console.error("Failed to save quotation:", error)
        toast.error("Failed to save quotation.")
      }
    },
    [
      isEdit,
      identifer,
      vehicleSpecs,
      priceItems,
      multiCarSpecs,
      multiCarMode,
      totalAmount,
      amountInWords,
      selectedCustomer,
      selectedVehicle,
      createQuotation,
      editQuotation,
      navigate,
    ]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit,
  })

  // Helper function to get field error state
  const getFieldError = fieldName => {
    return {
      invalid: formik.touched[fieldName] && !!formik.errors[fieldName],
      feedback: formik.touched[fieldName] && formik.errors[fieldName],
    }
  }

  // Button setup
  const buttonText = useMemo(() => {
    const loading = createLoading || editLoading
    return `${isEdit ? "Save Changes" : "Create Quotation"}${
      loading ? "..." : ""
    }`
  }, [isEdit, createLoading, editLoading])

  const isSubmitDisabled = useMemo(
    () => createLoading || editLoading,
    [createLoading, editLoading]
  )

  // Render price item
  const renderPriceItem = (item, index) => (
    <Row className="g-2 mb-2">
      <Col md={7}>
        <Input
          type="text"
          value={item.description}
          onChange={e =>
            updatePriceItem(item.id, "description", e.target.value)
          }
          placeholder="Item description"
          className="form-control-sm"
        />
      </Col>
      <Col md={5}>
        <Input
          type="number"
          value={item.amount}
          onChange={e => updatePriceItem(item.id, "amount", e.target.value)}
          placeholder="Amount"
          min="0"
          step="0.01"
          className="form-control-sm"
        />
      </Col>
    </Row>
  )

  const { customerSearchAPI: handleSearch, loading: isSearching } =
    useCustomerSearchAPI({
      all: true,
      limit: 5,
    })

  // Customer API
  const customerSearchAPI = async searchTerm => {
    return await handleSearch(searchTerm)
  }

  // Set selectedCustomer
  useEffect(() => {
    if (customerData?.data && !customerLoading) {
      setSelectedCustomer(customerData?.data)
    }
  }, [customerData?.data, customerLoading])
  // Set selectedVehicle
  useEffect(() => {
    if (vehicleData?.data && !vehicleLoading) {
      setSelectedVehicle(vehicleData?.data)
    }
  }, [vehicleData?.data, vehicleLoading])

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Quotations" breadcrumbItem="Add / Edit Quotation" />

        {/* A4 Paper Style Container */}
        <div className="a4-paper-container">
          <div className="a4-paper">
            <Row className="g-0">
              <Col lg={12}>
                <Card className="border-0 shadow-none">
                  <CardBody className="p-4 p-md-3 p-lg-4">
                    {/* Header Section - Company Info */}
                    <Row className="border-bottom pb-3 pb-md-2 mb-4 mb-md-3 text-center">
                      <Col xs={12}>
                        <h2 className="text-primary mb-1 fs-4 fs-md-3 fs-lg-2">
                          AUROBIL CARS
                        </h2>
                        <p className="text-muted mb-1">
                          Trusted Car Sales Center
                        </p>
                        <p className="text-muted mb-0">
                          <small>
                            123 Auto Street, Dhaka, Bangladesh | Phone: +880
                            1234-567890
                          </small>
                        </p>
                      </Col>
                    </Row>

                    <QuotationHeader
                      formik={formik}
                      getFieldError={getFieldError}
                      formatSinceDate={formatSinceDate}
                    />

                    <Form onSubmit={formik.handleSubmit}>
                      {/* Customer Info */}
                      <Row className="mb-4">
                        <Col xs={12}>
                          <CustomerSelector
                            formik={formik}
                            apiSearchFunction={customerSearchAPI}
                            isSearching={isSearching || customerLoading}
                            selectedCustomer={selectedCustomer}
                            setSelectedCustomer={setSelectedCustomer}
                            existCustomerData={customerData?.data}
                          />
                        </Col>
                      </Row>
                      {/* Subject */}
                      <Row className="mb-3">
                        <Col xs={12}>
                          <FormGroup>
                            <Label className="fw-bold">Subject:</Label>
                            <Input
                              type="text"
                              name="subject"
                              value={formik.values.subject}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              placeholder="Quotation subject..."
                              className="form-control-sm"
                              {...getFieldError("subject")}
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                      {/* Salutation */}
                      <Row className="mb-3">
                        <Col xs={12}>
                          <FormGroup>
                            <Label className="fw-bold">Salutation:</Label>
                            <DynamicField
                              type="richtext"
                              name="body_text"
                              value={formik.values.body_text}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              setFieldValue={formik.setFieldValue}
                              placeholder="Enter customer details..."
                              required
                              height={100}
                              invalid={
                                formik.touched.body_text &&
                                Boolean(formik.errors.body_text)
                              }
                              feedback={
                                formik.touched.body_text &&
                                formik.errors.body_text
                              }
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                      {/* Vehicle Specifications */}
                      <VehicleSpecification
                        onVehicleSpecsChange={setVehicleSpecs}
                        onMultiCarSpecsChange={setMultiCarSpecs}
                        onMultiCarModeChange={setMultiCarMode}
                        initialMultiCarMode={multiCarMode}
                        initialVehicleSpecs={vehicleSpecs}
                        initialMultiCarSpecs={multiCarSpecs}
                        selectedVehicle={selectedVehicle}
                        setSelectedVehicle={setSelectedVehicle}
                        existVehicleData={vehicleData?.data}
                        existVehicleLoading={vehicleLoading}
                      />
                      {/* Price Breakdown */}
                      <Row className="mb-4">
                        <Col xs={12}>
                          <Label className="fw-bold mb-3">
                            Price Breakdown:
                          </Label>
                          {!multiCarMode && (
                            <Card className="border">
                              <CardBody className="p-3">
                                <DraggableList
                                  items={priceItems}
                                  onDragEnd={() => {}}
                                  onAdd={() =>
                                    addPriceItem({ description: "", amount: 0 })
                                  }
                                  onRemove={removePriceItem}
                                  addButtonText="Add Cost Item"
                                  renderItem={renderPriceItem}
                                  minItems={1}
                                  compact
                                />
                              </CardBody>
                            </Card>
                          )}

                          {/* Total Summary */}
                          <Card className="mt-3 border-success">
                            <CardBody className="p-3">
                              <Row className="g-2">
                                <Col xs={6}>
                                  <strong>Total Amount:</strong>
                                </Col>
                                <Col xs={6} className="text-end">
                                  <strong className="text-success">
                                    BDT {totalAmount.toLocaleString()}
                                  </strong>
                                </Col>
                                <Col xs={12}>
                                  <small className="text-muted">
                                    <i className="bx bx-text me-1"></i>
                                    {amountInWords}
                                  </small>
                                </Col>
                              </Row>
                            </CardBody>
                          </Card>
                        </Col>
                      </Row>
                      {/* Payment Terms */}
                      <Row className="mb-4">
                        <Col xs={12}>
                          <FormGroup>
                            <Label className="fw-bold">Payment Terms:</Label>
                            <DynamicField
                              type="richtext"
                              name="payment_terms"
                              value={formik.values.payment_terms}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              setFieldValue={formik.setFieldValue}
                              placeholder="Enter customer details..."
                              required
                              height={100}
                              invalid={
                                formik.touched.payment_terms &&
                                Boolean(formik.errors.payment_terms)
                              }
                              feedback={
                                formik.touched.payment_terms &&
                                formik.errors.payment_terms
                              }
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                      {/* Closing Remarks */}
                      <Row className="mb-4">
                        <Col xs={12}>
                          <FormGroup>
                            <Label className="fw-bold">Closing Remarks:</Label>
                            <DynamicField
                              type="richtext"
                              name="closing_text"
                              value={formik.values.closing_text}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              setFieldValue={formik.setFieldValue}
                              placeholder="Enter customer details..."
                              required
                              height={100}
                              invalid={
                                formik.touched.closing_text &&
                                Boolean(formik.errors.closing_text)
                              }
                              feedback={
                                formik.touched.closing_text &&
                                formik.errors.closing_text
                              }
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                      {/* Signature Section */}
                      <Row className="mt-5">
                        <Col xs={6} className="text-center">
                          <div className="signature-section">
                            <div
                              className="border-top pt-3 mt-3"
                              style={{ borderType: "dashed" }}
                            >
                              <Input
                                type="text"
                                name="signatory_name"
                                value={formik.values.signatory_name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                placeholder="Signatory name"
                                className="form-control-sm text-center border-0 fw-bold"
                                {...getFieldError("signatory_name")}
                              />
                              <Input
                                type="text"
                                name="signatory_designation"
                                value={formik.values.signatory_designation}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                placeholder="Designation"
                                className="form-control-sm text-center border-0"
                                {...getFieldError("signatory_designation")}
                              />
                            </div>
                          </div>
                        </Col>
                        <Col xs={6} className="text-center">
                          <div className="signature-section">
                            <div className="border-top pt-3 mt-3">
                              <Input
                                type="text"
                                name="customer_signatory_name"
                                value={formik.values.customer_signatory_name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                placeholder="Customer Name"
                                className="form-control-sm text-center border-0 fw-bold"
                                {...getFieldError("customer_signatory_name")}
                              />
                            </div>
                          </div>
                        </Col>
                      </Row>
                      {/* Action Buttons */}
                      <div className="mt-4 pt-3 border-top">
                        <Col xs={12} className="text-center">
                          <div className="d-flex flex-column flex-sm-row justify-content-center gap-2">
                            <Button
                              type="button"
                              color="outline-danger"
                              size={isMobile ? "sm" : "md"}
                              onClick={() => navigate("/quotation-list")}
                              className="flex-fill flex-sm-grow-0"
                            >
                              <i className="bx bx-x me-2"></i>
                              Cancel
                            </Button>
                            <Button
                              type="submit"
                              color="primary"
                              size={isMobile ? "sm" : "md"}
                              disabled={isSubmitDisabled}
                              className="flex-fill flex-sm-grow-0"
                            >
                              <i className="bx bx-check me-2"></i>
                              {buttonText}
                            </Button>
                          </div>
                        </Col>
                      </div>
                    </Form>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </div>
        </div>
      </Container>
    </div>
  )
}

export default QuotationAddEdit
